import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useLocation } from '@reach/router'
import '../../../styles/external/wp_styles.css'

import { useHrefLangApiData } from '../../../hooks/graphqlStaticQuery/useHrefLangApiData'
import { useSeoPagesApiData } from '../../../hooks/graphqlStaticQuery/useSeoPagesApiData'
import { useSeoApiData } from '../../../hooks/graphqlStaticQuery/useSeoApiData'

import { langTags } from '../../../utils/constants'
import { isBrowser } from '../../../utils/generic'
import { If } from '../../atoms/if'
import useQueryParams from '../../../hooks/useQueryParams'
import { getWebsiteUrl } from '../../../utils/getWebsiteUrl'
import { addTrailingSlash } from '../../../utils/addTrailingSlash'
import { removeDuplicateLocaleFromPathname } from '../../../utils/removeLocaleFromPathname'
import headFeatureFlags from '../../../config/headFeatureFlags.json'
import { generateBreadcrumbJsonLd } from '../../../utils/seoSchemaUtils'
import { usePageExists } from '../../../hooks/usePageExists'
import { useNoIndex } from '../../../hooks/useNoIndex'

const locale = process.env.GATSBY_INTL_LOCALE

function Seo({
  hrefLangPage,
  yoast,
  title,
  description,
  href_langs,
  children,
  aditionalScriptLinks,
  noindex,
  pathname,
  disableCanonicalSSR,
  pageType,
}) {
  const { page } = useQueryParams()
  const { site, slugsData } = useSeoApiData()
  const isSEOEnabled = headFeatureFlags?.isSEOEnabled
  const { hrefLangApiData } = useHrefLangApiData({
    hrefLangPage,
    href_langs,
    slugsData,
  })
  const { seoContent, content } = useSeoPagesApiData(true)
  const location = useLocation()
  const websiteURL = getWebsiteUrl()
  const pageExist = usePageExists()
  const hrefLangs = []

  const sortSearchParams = (params) => {
    return params
      ?.replaceAll('amp;', '')
      ?.split('?')?.[1]
      ?.split('&')
      ?.sort()
      ?.join('&')
      ?.replaceAll('/', '')
  }

  const currentPath = sortSearchParams(location?.search)

  const findSeoPath = seoContent?.find((item) => {
    return sortSearchParams(item?.path) === currentPath
  })

  const seoPath = findSeoPath?.seo_path
    ? `${websiteURL}${findSeoPath?.seo_path}`
        ?.replaceAll('//', '/')
        ?.replaceAll('http:/', 'http://')
        ?.replaceAll('https:/', 'https://')
    : null

  const normalizedSeoPath = removeDuplicateLocaleFromPathname(seoPath)

  seoContent.forEach((seoPage) => {
    if (location?.href?.includes(seoPage.path)) {
      const index = location?.href?.indexOf(seoPage.path)
      const locationPath = location?.href?.slice(index)
      if (seoPage.path === '/' && location?.href === websiteURL) {
        hrefLangs.push(...seoPage.href_langs)
      } else if (locationPath === seoPage.path) {
        hrefLangs.push(...seoPage.href_langs)
      }
    }
  })

  if ((!yoast || !yoast?.yoast_head_json?.description) && content)
    yoast = { yoast_head_json: content?.yoast_head?.json }
  if (hrefLangPage) {
    const route = slugsData[0].slugs.filter((slug) => {
      return slug.generic_route === hrefLangPage
    })[0]
    if (route?.href_langs) hrefLangs.push(...(route?.href_langs ?? []))
  }
  if (href_langs?.length > 0) hrefLangs.push(...href_langs)

  const { doNotIndex } = useNoIndex(noindex, pageType, yoast)

  const metaTitle =
    yoast?.yoast_head_json?.og_title ||
    yoast?.yoast_head_json?.title ||
    title ||
    ''

  const metaDescription =
    yoast?.yoast_head_json?.og_description ||
    yoast?.yoast_head_json?.description ||
    description ||
    site.siteMetadata.description

  const ogMetaType = yoast?.yoast_head_json?.og_type || 'website'
  const ogMetaLang =
    yoast?.yoast_head_json?.og_locale?.replace('_', '-') || langTags[locale]

  const renderCanonical = () => {
    if (isBrowser() && window?.location?.search.indexOf('sportId') < 0) {
      const href = `${window?.location?.href.replace(
        window?.location?.search,
        ''
      )}`

      const normalizedHref = removeDuplicateLocaleFromPathname(href)

      return <link rel="canonical" href={addTrailingSlash(normalizedHref)} />
    }
  }
  const generateUrl = () => {
    let baseUrl = `${websiteURL}${pathname?.replace('/', '')}`

    if (page) {
      baseUrl = `${baseUrl}?page=${page}`
    }

    return removeDuplicateLocaleFromPathname(baseUrl)
  }

  const url = generateUrl()

  const processSchemas = (yoast, pathname, websiteURL, title, pageExist) => {
    try {
      if (!yoast?.yoast_head_json?.schema) {
        return [null, null]
      }

      const yoastSchema = JSON.parse(yoast.yoast_head_json.schema)

      const filteredGraph = yoastSchema['@graph'].filter(
        (item) => item['@type'] !== 'BreadcrumbList'
      )

      const webPage = filteredGraph.find((item) => item['@type'] === 'WebPage')
      if (webPage) {
        delete webPage.breadcrumb
      }

      const cleanYoastSchema = {
        ...yoastSchema,
        '@graph': filteredGraph,
      }

      const breadcrumbsSchema = generateBreadcrumbJsonLd(
        pathname,
        websiteURL,
        title,
        pageExist
      )

      return [cleanYoastSchema, breadcrumbsSchema]
    } catch (error) {
      console.error('Error processing schemas:', error)
      return [null, null]
    }
  }

  const [cleanYoastSchema, breadcrumbsSchema] = useMemo(() => {
    if (!pathname || !websiteURL) {
      return [null, null]
    }

    return processSchemas(yoast, pathname, websiteURL, title, pageExist)
  }, [yoast, pathname, websiteURL, title, pageExist])

  const renderScriptSchema = ({ id, data }) => {
    return (
      <script
        id={id}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: data,
        }}
      />
    )
  }

  return (
    <>
      <meta charSet="UTF-8" />
      <meta
        id="viewport"
        name="viewport"
        content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
      />
      <meta
        httpEquiv="cache-control"
        content="no-cache, no-store, must-revalidate"
      />
      <meta httpEquiv="expires" content="0" />
      <meta httpEquiv="pragma" content="no-cache" />
      <If
        condition={!!isSEOEnabled}
        render={() => (
          <>
            <title>{metaTitle}</title>

            <If
              condition={!!yoast?.yoast_head_json?.script}
              render={() => <script>{yoast?.yoast_head_json?.script}</script>}
            />

            <If
              condition={!!cleanYoastSchema}
              render={() =>
                renderScriptSchema({
                  id: 'yoast-schema',
                  data: JSON.stringify(cleanYoastSchema),
                })
              }
            />

            <If
              condition={!!breadcrumbsSchema}
              render={() =>
                renderScriptSchema({
                  id: 'breadcrumbs-schema',
                  data: JSON.stringify(breadcrumbsSchema),
                })
              }
            />

            <If
              condition={doNotIndex}
              render={() => (
                <meta name="robots" content="noindex, nofollow, noarchive" id="meta-robots" />
              )}
              renderElse={() => (
                <meta
                  name="robots"
                  content={yoast?.yoast_head_json?.robots?.index}
                  id="meta-robots"
                />
              )}
            />
            <If
              condition={!!yoast?.yoast_head_json?.og_image}
              render={() => (
                <meta
                  name="og:image"
                  content={yoast?.yoast_head_json?.og_image?.[0]?.url}
                />
              )}
            />

            {aditionalScriptLinks.map((link, i) => (
              <script key={`script-${i}`} src={link} />
            ))}

            <meta
              name="og:title"
              content={yoast?.yoast_head_json?.og_title || metaTitle}
            />
            <meta name="description" content={metaDescription} />
            <meta
              name="og:description"
              content={
                yoast?.yoast_head_json?.og_description || metaDescription
              }
            />
            <meta
              name="og:site_name"
              content={yoast?.yoast_head_json?.og_site_name}
            />
            <meta name="og:url" content={url} />
            <meta name="og:locale" content={ogMetaLang} />
            <meta name="og:type" content={ogMetaType} />
            <meta name="twitter:card" content={'summary'} />
            <meta
              name="twitter:creator"
              content={site.siteMetadata?.author || ''}
            />
            <meta name="twitter:title" content={metaTitle} />
            <meta name="twitter:description" content={metaDescription} />

            {children}

            {hrefLangApiData
              .filter((hrefLang) => hrefLang?.href_lang)
              .map((hrefLang, i) => (
                <link
                  rel="alternate"
                  href={hrefLang?.url}
                  hrefLang={hrefLang?.href_lang}
                  key={`href-lang-${i}`}
                />
              ))}

            <If
              condition={!disableCanonicalSSR}
              render={() => (
                <link rel="canonical" href={addTrailingSlash(url)} />
              )}
              renderElse={() => (
                <>
                  <If
                    condition={
                      findSeoPath &&
                      seoPath &&
                      typeof window !== 'undefined' &&
                      window?.location?.search.indexOf('sportId') >= 0
                    }
                    render={() => (
                      <link
                        rel="canonical"
                        href={addTrailingSlash(normalizedSeoPath)}
                      />
                    )}
                  />

                  {renderCanonical()}
                </>
              )}
            />
          </>
        )}
      />
    </>
  )
}

Seo.defaultProps = {
  description: '',
  aditionalScriptLinks: [],
  title: '',
  image: null,
  pathname: '',
  disableCanonicalSSR: false,
  noindex: false,
}

Seo.propTypes = {
  hrefLangPage: PropTypes.string,
  yoast: PropTypes.object,
  title: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.any,
  href_langs: PropTypes.array,
  aditionalScriptLinks: PropTypes.array,
  image: PropTypes.string,
  pathname: PropTypes.string,
  disableCanonicalSSR: PropTypes.bool,
  noindex: PropTypes.bool,
}

export { Seo }
